import { EventMsgError } from "./event-msg-error.js";

/**
 * Error related to transport layer operations (connection, sending, receiving)
 */
export class TransportError extends EventMsgError {
  constructor(
    message: string,
    options: {
      context?: Record<string, unknown>;
      solutions?: string[];
      cause?: Error;
    } = {}
  ) {
    super(message, "TRANSPORT_ERROR", {
      ...options,
      solutions: options.solutions || [
        "Check transport connection",
        "Verify transport configuration",
        "Retry the operation",
      ],
    });
  }
}

/**
 * Transport connection failed
 */
export class ConnectionError extends TransportError {
  constructor(
    message = "Failed to connect to transport",
    options: {
      context?: Record<string, unknown>;
      cause?: Error;
    } = {}
  ) {
    super(message, {
      ...options,
      solutions: [
        "Check if the device is available",
        "Verify connection parameters",
        "Ensure no other process is using the transport",
        "Check device permissions",
      ],
    });

    this.code = "CONNECTION_ERROR";
  }
}

/**
 * Transport send operation failed
 */
export class SendError extends TransportError {
  constructor(
    message = "Failed to send data through transport",
    options: {
      context?: Record<string, unknown>;
      cause?: Error;
    } = {}
  ) {
    super(message, {
      ...options,
      solutions: [
        "Check transport connection status",
        "Verify message size is within limits",
        "Retry sending the message",
        "Check for transport buffer overflow",
      ],
    });

    this.code = "SEND_ERROR";
  }
}

/**
 * Transport disconnection error
 */
export class DisconnectionError extends TransportError {
  constructor(
    message = "Transport disconnected unexpectedly",
    options: {
      context?: Record<string, unknown>;
      cause?: Error;
    } = {}
  ) {
    super(message, {
      ...options,
      solutions: [
        "Check physical connection",
        "Implement reconnection logic",
        "Monitor connection status",
        "Handle graceful disconnection",
      ],
    });

    this.code = "DISCONNECTION_ERROR";
  }
}
